import React, { useState, useEffect } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import './Admin.css';

const firebaseConfig = {
  apiKey: "AIzaSyB9kanevbsolQ8-KgyzHUg09ZkXBAOdGKg",
  authDomain: "flow-mobile-e7a07.firebaseapp.com",
  projectId: "flow-mobile-e7a07",
  storageBucket: "flow-mobile-e7a07.appspot.com",
  messagingSenderId: "15826011991",
  appId: "1:15826011991:web:b4f3455652f535e2252012",
};

const adminApp = getApps().find(a => a.name === 'admin') || initializeApp(firebaseConfig, 'admin');
const adminAuth = getAuth(adminApp);
const adminDb = getFirestore(adminApp);


const CLASSES = [
  { id: 'force',   label: 'Force',    color: '#e74c3c' },
  { id: 'agility', label: 'Agilité',  color: '#e67e22' },
  { id: 'ability', label: 'Habileté', color: '#3498db' },
];

interface BilingualText { fr: string; en: string; }

interface Training {
  id: string;
  num?: number;
  title: BilingualText;
  description: BilingualText;
  criteriaEval?: BilingualText[];
  evaluationVideo?: {
    url?: BilingualText | string;
    consignes?: BilingualText;
  };
}

interface Stage { id: string; num?: number; title?: BilingualText | string; }
interface Level { id: string; num?: number; }

function getStageLabel(stage: Stage): string {
  const title = typeof stage.title === 'object' ? stage.title?.fr : stage.title;
  if (title) return stage.num ? `${stage.num}. ${title}` : title;
  if (stage.num) return `Chapitre ${stage.num}`;
  return stage.id;
}

function getUrlLang(url: BilingualText | string | undefined, lang: 'fr' | 'en'): string {
  if (!url) return '';
  if (typeof url === 'object') return url[lang] || '';
  return url;
}

function deepUpdate(obj: any, path: string[], value: string): any {
  const clone = JSON.parse(JSON.stringify(obj));
  let cur = clone;
  for (let i = 0; i < path.length - 1; i++) {
    if (typeof cur[path[i]] !== 'object' || cur[path[i]] === null) cur[path[i]] = {};
    cur = cur[path[i]];
  }
  cur[path[path.length - 1]] = value;
  return clone;
}

function normalizeForEdit(training: Training): Training {
  const t: Training = JSON.parse(JSON.stringify(training));
  if (t.evaluationVideo?.url && typeof t.evaluationVideo.url === 'string') {
    const url = t.evaluationVideo.url as string;
    t.evaluationVideo.url = { fr: url, en: url };
  }
  return t;
}

const Admin: React.FC = () => {
  const [user, setUser]           = useState<User | null>(null);
  const [isAdmin, setIsAdmin]     = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [authError, setAuthError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [selectedClass, setSelectedClass] = useState('force');
  const [stages, setStages]       = useState<Stage[]>([]);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [levels, setLevels]       = useState<Level[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const [editingTraining, setEditingTraining] = useState<Training | null>(null);
  const [saving, setSaving]       = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Auth listener — vérifie isAdmin dans Firestore après connexion
  useEffect(() => {
    return onAuthStateChanged(adminAuth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const userDoc = await getDoc(doc(adminDb, 'users', u.uid));
          setIsAdmin(userDoc.exists() && userDoc.data()?.isAdmin === true);
        } catch {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setAuthError('');
    try {
      await signInWithEmailAndPassword(adminAuth, email, password);
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/unauthorized-domain') {
        setAuthError('Domaine non autorisé dans Firebase Console (Authorized Domains).');
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setAuthError('Mot de passe incorrect.');
      } else if (code === 'auth/user-not-found') {
        setAuthError('Aucun compte avec cet email.');
      } else if (code === 'auth/too-many-requests') {
        setAuthError('Trop de tentatives. Réessaie dans quelques minutes.');
      } else {
        setAuthError(`Erreur : ${code || err?.message || 'inconnue'}`);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    signOut(adminAuth);
    setSelectedStage(null);
    setSelectedLevel(null);
    setTrainings([]);
  };

  // Load stages when class changes
  useEffect(() => {
    if (!isAdmin) return;
    setSelectedStage(null);
    setSelectedLevel(null);
    setTrainings([]);
    setLoadingData(true);
    getDocs(collection(adminDb, 'classes', selectedClass, 'stages'))
      .then(snap => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Stage));
        data.sort((a, b) => (a.num || 0) - (b.num || 0));
        setStages(data);
      })
      .finally(() => setLoadingData(false));
  }, [selectedClass, isAdmin]);

  // Load levels when stage changes
  useEffect(() => {
    if (!selectedStage || !isAdmin) return;
    setSelectedLevel(null);
    setTrainings([]);
    setLoadingData(true);
    getDocs(collection(adminDb, 'classes', selectedClass, 'stages', selectedStage, 'levels'))
      .then(snap => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Level));
        data.sort((a, b) => (a.num || 0) - (b.num || 0));
        setLevels(data);
      })
      .finally(() => setLoadingData(false));
  }, [selectedStage, selectedClass, isAdmin]);

  // Load trainings when level changes
  useEffect(() => {
    if (!selectedLevel || !selectedStage || !isAdmin) return;
    setLoadingData(true);
    getDocs(collection(adminDb, 'classes', selectedClass, 'stages', selectedStage, 'levels', selectedLevel, 'trainings'))
      .then(snap => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Training));
        data.sort((a, b) => (a.num || 0) - (b.num || 0));
        setTrainings(data);
      })
      .finally(() => setLoadingData(false));
  }, [selectedLevel, selectedStage, selectedClass, isAdmin]);

  const handleSave = async () => {
    if (!editingTraining || !selectedStage || !selectedLevel) return;
    setSaving(true);
    try {
      const ref = doc(adminDb, 'classes', selectedClass, 'stages', selectedStage, 'levels', selectedLevel, 'trainings', editingTraining.id);

      // Dot-notation : seuls les champs visibles dans le formulaire sont écrits.
      // Tout autre champ Firestore inconnu reste intact.
      const update: Record<string, any> = {
        'title.fr': editingTraining.title?.fr || '',
        'title.en': editingTraining.title?.en || '',
        'description.fr': editingTraining.description?.fr || '',
        'description.en': editingTraining.description?.en || '',
        'criteriaEval': editingTraining.criteriaEval || [],
      };

      const urlFr = getUrlLang(editingTraining.evaluationVideo?.url, 'fr');
      const urlEn = getUrlLang(editingTraining.evaluationVideo?.url, 'en');
      if (urlFr || urlEn) {
        update['evaluationVideo.url'] = { fr: urlFr, en: urlEn };
      }

      const consignesFr = editingTraining.evaluationVideo?.consignes?.fr || '';
      const consignesEn = editingTraining.evaluationVideo?.consignes?.en || '';
      if (consignesFr || consignesEn) {
        update['evaluationVideo.consignes'] = { fr: consignesFr, en: consignesEn };
      }

      await updateDoc(ref, update);
      setTrainings(prev => prev.map(t => t.id === editingTraining.id ? editingTraining : t));
      setSaveSuccess(true);
      setTimeout(() => { setSaveSuccess(false); setEditingTraining(null); }, 1400);
    } catch {
      alert('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path: string[], value: string) => {
    if (!editingTraining) return;
    setEditingTraining(deepUpdate(editingTraining, path, value));
  };

  const updateCriteria = (index: number, lang: 'fr' | 'en', value: string) => {
    if (!editingTraining) return;
    const criteria = [...(editingTraining.criteriaEval || [])];
    criteria[index] = { ...criteria[index], [lang]: value };
    setEditingTraining({ ...editingTraining, criteriaEval: criteria });
  };

  const addCriteria = () => {
    if (!editingTraining) return;
    setEditingTraining({ ...editingTraining, criteriaEval: [...(editingTraining.criteriaEval || []), { fr: '', en: '' }] });
  };

  const removeCriteria = (index: number) => {
    if (!editingTraining) return;
    const criteria = [...(editingTraining.criteriaEval || [])];
    criteria.splice(index, 1);
    setEditingTraining({ ...editingTraining, criteriaEval: criteria });
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (authLoading) return <div className="admin-loading">Chargement…</div>;

  // ── Login ────────────────────────────────────────────────────────────────
  if (!user || !isAdmin) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-logo">⚡ FlowXP Admin</div>
          <h2>Connexion</h2>
          <form onSubmit={handleLogin}>
            <div className="admin-field">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@flowxp.com" required />
            </div>
            <div className="admin-field">
              <label>Mot de passe</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            {authError && <div className="admin-auth-error">{authError}</div>}
            <button type="submit" className="admin-btn-primary" disabled={loginLoading}>
              {loginLoading ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const currentClass = CLASSES.find(c => c.id === selectedClass)!;
  const currentStage = stages.find(s => s.id === selectedStage);
  const currentLevel = levels.find(l => l.id === selectedLevel);

  // ── Main ─────────────────────────────────────────────────────────────────
  return (
    <div className="admin-container">
      <div className="admin-header">
        <span className="admin-header-title">⚡ FlowXP — Éditeur de contenus</span>
        <button className="admin-logout-btn" onClick={handleLogout}>Déconnexion</button>
      </div>

      <div className="admin-layout">
        {/* Sidebar */}
        <div className="admin-sidebar">

          <div className="admin-sidebar-section">
            <div className="admin-sidebar-label">Classe</div>
            {CLASSES.map(cls => (
              <button
                key={cls.id}
                className={`admin-sidebar-item ${selectedClass === cls.id ? `active ${cls.id}` : ''}`}
                onClick={() => setSelectedClass(cls.id)}
              >
                {cls.label}
              </button>
            ))}
          </div>

          {stages.length > 0 && (
            <>
              <div className="admin-sidebar-divider" />
              <div className="admin-sidebar-section">
                <div className="admin-sidebar-label">Chapitre</div>
                {stages.map(stage => (
                  <button
                    key={stage.id}
                    className={`admin-sidebar-item ${selectedStage === stage.id ? 'active' : ''}`}
                    onClick={() => setSelectedStage(stage.id)}
                  >
                    {getStageLabel(stage)}
                  </button>
                ))}
              </div>
            </>
          )}

          {levels.length > 0 && (
            <>
              <div className="admin-sidebar-divider" />
              <div className="admin-sidebar-section">
                <div className="admin-sidebar-label">Niveau</div>
                {levels.map(level => (
                  <button
                    key={level.id}
                    className={`admin-sidebar-item ${selectedLevel === level.id ? 'active' : ''}`}
                    onClick={() => setSelectedLevel(level.id)}
                  >
                    Niveau {level.num || level.id}
                  </button>
                ))}
              </div>
            </>
          )}

        </div>

        {/* Main content */}
        <div className="admin-main">
          {!selectedStage && (
            <div className="admin-placeholder">Sélectionne un chapitre pour commencer</div>
          )}
          {selectedStage && !selectedLevel && (
            <div className="admin-placeholder">Sélectionne un niveau</div>
          )}
          {selectedLevel && (
            <>
              <div className="admin-main-header">
                <h2>
                  <span style={{ color: currentClass.color }}>{currentClass.label}</span>
                  <span className="admin-breadcrumb-sep">›</span>
                  {currentStage ? getStageLabel(currentStage) : selectedStage}
                  <span className="admin-breadcrumb-sep">›</span>
                  Niveau {currentLevel?.num || selectedLevel}
                </h2>
                <span className="admin-count">
                  {loadingData ? '…' : `${trainings.length} exercice${trainings.length > 1 ? 's' : ''}`}
                </span>
              </div>

              {loadingData ? (
                <div className="admin-placeholder">Chargement…</div>
              ) : trainings.length === 0 ? (
                <div className="admin-placeholder">Aucun exercice dans ce niveau</div>
              ) : (
                <div className="admin-trainings-list">
                  {trainings.map(training => (
                    <div key={training.id} className="admin-training-card">
                      <div className="admin-training-info">
                        <div className="admin-training-num">#{training.num ?? '—'}</div>
                        <div>
                          <div className="admin-training-title">{training.title?.fr || 'Sans titre'}</div>
                          <div className="admin-training-subtitle">{training.title?.en || ''}</div>
                        </div>
                      </div>
                      <button
                        className="admin-edit-btn"
                        onClick={() => setEditingTraining(normalizeForEdit(training))}
                      >
                        Modifier
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingTraining && (
        <div className="admin-modal-overlay" onClick={() => setEditingTraining(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>

            <div className="admin-modal-header">
              <h3>Exercice #{editingTraining.num} — {editingTraining.title?.fr || 'Sans titre'}</h3>
              <button className="admin-modal-close" onClick={() => setEditingTraining(null)}>×</button>
            </div>

            <div className="admin-modal-body">

              {/* Titre */}
              <div className="admin-form-group">
                <label className="admin-form-label">Titre</label>
                <div className="admin-bilingual">
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag">FR</span>
                    <input value={editingTraining.title?.fr || ''} onChange={e => updateField(['title', 'fr'], e.target.value)} placeholder="Titre en français" />
                  </div>
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag en">EN</span>
                    <input value={editingTraining.title?.en || ''} onChange={e => updateField(['title', 'en'], e.target.value)} placeholder="Title in English" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="admin-form-group">
                <label className="admin-form-label">Description</label>
                <div className="admin-bilingual">
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag">FR</span>
                    <textarea rows={3} value={editingTraining.description?.fr || ''} onChange={e => updateField(['description', 'fr'], e.target.value)} placeholder="Description en français" />
                  </div>
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag en">EN</span>
                    <textarea rows={3} value={editingTraining.description?.en || ''} onChange={e => updateField(['description', 'en'], e.target.value)} placeholder="Description in English" />
                  </div>
                </div>
              </div>

              {/* Critères d'évaluation */}
              <div className="admin-form-group">
                <label className="admin-form-label">Critères d'évaluation</label>
                {(editingTraining.criteriaEval || []).map((crit, i) => (
                  <div key={i} className="admin-criteria-row">
                    <span className="admin-criteria-num">{i + 1}.</span>
                    <div className="admin-criteria-fields admin-bilingual" style={{ flex: 1 }}>
                      <div className="admin-lang-field">
                        <span className="admin-lang-tag">FR</span>
                        <input value={crit.fr || ''} onChange={e => updateCriteria(i, 'fr', e.target.value)} placeholder="Critère en français" />
                      </div>
                      <div className="admin-lang-field">
                        <span className="admin-lang-tag en">EN</span>
                        <input value={crit.en || ''} onChange={e => updateCriteria(i, 'en', e.target.value)} placeholder="Criterion in English" />
                      </div>
                    </div>
                    <button className="admin-remove-btn" onClick={() => removeCriteria(i)}>×</button>
                  </div>
                ))}
                <button className="admin-add-btn" onClick={addCriteria}>+ Ajouter un critère</button>
              </div>

              {/* Vidéo de référence */}
              <div className="admin-form-group">
                <label className="admin-form-label">Vidéo de référence — URL</label>
                <div className="admin-bilingual" style={{ marginBottom: 10 }}>
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag">FR</span>
                    <input
                      value={getUrlLang(editingTraining.evaluationVideo?.url, 'fr')}
                      onChange={e => updateField(['evaluationVideo', 'url', 'fr'], e.target.value)}
                      placeholder="https://… (FR)"
                    />
                  </div>
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag en">EN</span>
                    <input
                      value={getUrlLang(editingTraining.evaluationVideo?.url, 'en')}
                      onChange={e => updateField(['evaluationVideo', 'url', 'en'], e.target.value)}
                      placeholder="https://… (EN)"
                    />
                  </div>
                </div>
                <label className="admin-form-label">Consignes</label>
                <div className="admin-bilingual">
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag">FR</span>
                    <textarea rows={2} value={editingTraining.evaluationVideo?.consignes?.fr || ''} onChange={e => updateField(['evaluationVideo', 'consignes', 'fr'], e.target.value)} placeholder="Consignes en français" />
                  </div>
                  <div className="admin-lang-field">
                    <span className="admin-lang-tag en">EN</span>
                    <textarea rows={2} value={editingTraining.evaluationVideo?.consignes?.en || ''} onChange={e => updateField(['evaluationVideo', 'consignes', 'en'], e.target.value)} placeholder="Instructions in English" />
                  </div>
                </div>
              </div>

            </div>

            <div className="admin-modal-footer">
              <button className="admin-btn-cancel" onClick={() => setEditingTraining(null)}>Annuler</button>
              <button
                className={`admin-btn-save${saveSuccess ? ' success' : ''}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Sauvegarde…' : saveSuccess ? '✓ Sauvegardé' : 'Sauvegarder'}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
